import type { ProfilePayoutDisplaySummary } from '../utils/profileAssetOwnerDisplay';
import { assetOwnerPayoutTransferDayOptionKey } from '../utils/assetOwnerPayoutBank';
import { useI18n } from '../i18n';
import styles from './ProfileMenu.module.css';

type ProfileHubPaymentContentProps = {
  payout: ProfilePayoutDisplaySummary | null;
  onEditPayment: () => void;
  successMessage?: string | null;
};

export function ProfileHubPaymentContent({
  payout,
  onEditPayment,
  successMessage,
}: ProfileHubPaymentContentProps) {
  const { t, language } = useI18n();

  const transferDayOptionKey =
    payout?.transferDay != null ? assetOwnerPayoutTransferDayOptionKey(payout.transferDay) : null;
  const transferDayLabel = transferDayOptionKey ? t(transferDayOptionKey) : null;

  const rows: { label: string; value: string | null }[] = payout
    ? [
        {
          label: t('register.assetOwnerPayout.accountHolderName'),
          value: payout.accountHolderName,
        },
        ...(payout.payoutCountry === 'IL'
          ? [
              { label: t('register.assetOwnerPayout.bankNumber'), value: payout.bankNumber },
              { label: t('register.assetOwnerPayout.branchNumber'), value: payout.branchNumber },
            ]
          : [
              { label: t('register.assetOwnerPayout.routingNumber'), value: payout.routingNumber },
              {
                label: t('register.assetOwnerPayout.accountType'),
                value:
                  payout.accountType === 'checking'
                    ? t('register.assetOwnerPayout.accountTypeChecking')
                    : payout.accountType === 'savings'
                      ? t('register.assetOwnerPayout.accountTypeSavings')
                      : null,
              },
            ]),
        { label: t('register.assetOwnerPayout.accountNumber'), value: payout.accountNumber },
        {
          label: t('register.assetOwnerPayout.transferDay'),
          value: transferDayLabel,
        },
      ]
    : [];

  return (
    <div
      className={[
        styles.hubDetailsPanel,
        language === 'he' ? styles.hubDetailsPanelRtl : styles.hubDetailsPanelLtr,
      ]
        .filter(Boolean)
        .join(' ')}
      dir={language === 'he' ? 'rtl' : 'ltr'}
    >
      {successMessage ? (
        <p className={styles.hubPaymentUpdatedNotice} role="status">
          {successMessage}
        </p>
      ) : null}
      <button
        type="button"
        className={styles.hubDetailsEditBtn}
        onClick={onEditPayment}
        aria-label={t('profile.hub.editPayment')}
      >
        <ProfileEditIcon />
        <span>{t('list.edit')}</span>
      </button>
      {!payout ? (
        <p className={styles.hubRowDetail} role="status">
          {t('profile.hub.paymentNotSet')}
        </p>
      ) : (
        <dl className={styles.hubDetailsList}>
          {rows.map((row) => {
            const isNumericValue =
              row.label === t('register.assetOwnerPayout.bankNumber') ||
              row.label === t('register.assetOwnerPayout.branchNumber') ||
              row.label === t('register.assetOwnerPayout.accountNumber') ||
              row.label === t('register.assetOwnerPayout.routingNumber');
            return (
              <div key={row.label} className={styles.hubDetailRow}>
                <dt className={styles.hubDetailLabel}>{row.label}</dt>
                <dd
                  className={[
                    styles.hubDetailValue,
                    isNumericValue ? styles.hubDetailValueLtr : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {row.value?.trim() || t('profile.field.notSet')}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}

function ProfileEditIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 20h9" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
